import React, { useState, useEffect } from 'react';
import { supabase } from '@/utils/supabase';
import { toast } from 'sonner';
import { Shield, Check, X, Loader2, Info } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { useRoleAccess } from '@/hooks/useRoleAccess';

interface Permission {
  id: string;
  name: string;
  description: string | null;
  category: string;
}

interface RolePermission {
  role: string;
  permission_id: string;
  enabled: boolean;
}

const ROLES = [
  { id: 'admin', name: 'Admin', color: 'bg-red-500' },
  { id: 'manager', name: 'Manager', color: 'bg-blue-500' },
  { id: 'loan_officer', name: 'Loan Officer', color: 'bg-green-500' },
  { id: 'underwriter', name: 'Underwriter', color: 'bg-orange-500' },
  { id: 'processor', name: 'Processor', color: 'bg-purple-500' },
  { id: 'compliance_specialist', name: 'Compliance Specialist', color: 'bg-indigo-500' },
  { id: 'closer', name: 'Closer', color: 'bg-teal-500' },
  { id: 'loan_officer_assistant', name: 'Loan Officer Assistant', color: 'bg-yellow-500' },
  { id: 'partner', name: 'Partner', color: 'bg-gray-500' },
  { id: 'recruiter', name: 'Recruiter', color: 'bg-pink-500' }
];

export const RolePermissionsMatrix: React.FC = () => {
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [rolePermissions, setRolePermissions] = useState<RolePermission[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [updating, setUpdating] = useState<string | null>(null);

  // Get refresh function for auto-refresh when impersonating
  const { refreshPermissions, isImpersonating } = useRoleAccess();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load all permissions
      const { data: permsData, error: permsError } = await supabase
        .from('permissions')
        .select('*')
        .order('category', { ascending: true })
        .order('name', { ascending: true });

      if (permsError) {
throw permsError;
}

      // Load role-permission mappings
      const { data: rolePermsData, error: rolePermsError } = await supabase
        .from('role_permissions')
        .select('role, permission_id, enabled');

      if (rolePermsError) {
throw rolePermsError;
}

      setPermissions(permsData || []);
      setRolePermissions(rolePermsData || []);
    } catch (error) {
      console.error('Error loading permissions:', error);
      toast.error('Failed to load permissions');
    } finally {
      setLoading(false);
    }
  };

  const hasPermission = (roleId: string, permissionId: string): boolean => {
    return rolePermissions.some(
      rp => rp.role === roleId && rp.permission_id === permissionId && rp.enabled
    );
  };

  const togglePermission = async (roleId: string, permissionId: string, currentValue: boolean) => {
    const key = `${roleId}-${permissionId}`;
    setUpdating(key);

    try {
      const { error } = await supabase.rpc('admin_set_role_permission', {
        p_role: roleId,
        p_permission_id: permissionId,
        p_enabled: !currentValue
      });
      if (error) {
        throw error;
      }

      if (currentValue) {
        // Update local state
        setRolePermissions(prev =>
          prev.map(rp =>
            rp.role === roleId && rp.permission_id === permissionId
              ? { ...rp, enabled: false }
              : rp
          )
        );

        toast.success(`Disabled permission for ${ROLES.find(r => r.id === roleId)?.name}`);
      } else {
        // Update local state
        const exists = rolePermissions.some(
          rp => rp.role === roleId && rp.permission_id === permissionId
        );

        if (exists) {
          setRolePermissions(prev =>
            prev.map(rp =>
              rp.role === roleId && rp.permission_id === permissionId
                ? { ...rp, enabled: true }
                : rp
            )
          );
        } else {
          setRolePermissions(prev => [
            ...prev,
            { role: roleId, permission_id: permissionId, enabled: true }
          ]);
        }

        toast.success(`Enabled permission for ${ROLES.find(r => r.id === roleId)?.name}`);
      }

      // Auto-refresh permissions when impersonating for immediate testing
      if (isImpersonating) {
        await refreshPermissions();
      }
    } catch (error) {
      console.error('Error toggling permission:', error);
      toast.error('Failed to update permission');
    } finally {
      setUpdating(null);
    }
  };

  const filteredPermissions = permissions.filter(
    perm =>
      perm.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      perm.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Group permissions by category
  const groupedPermissions = filteredPermissions.reduce((acc, perm) => {
    if (!acc[perm.category]) {
      acc[perm.category] = [];
    }
    acc[perm.category].push(perm);
    return acc;
  }, {} as Record<string, Permission[]>);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
          <Shield className="h-8 w-8 text-blue-600" />
          Role Permissions Matrix
        </h1>
        <p className="text-muted-foreground mt-2">
          Manage which permissions are assigned to each role. Changes take effect immediately.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Permission Management</CardTitle>
          <CardDescription>
            Check or uncheck boxes to enable/disable permissions for each role
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Search */}
          <div>
            <Input
              placeholder="Search permissions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="max-w-md"
            />
          </div>

          {/* Info Banner */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
            <Info className="h-5 w-5 text-blue-600 mt-0.5" />
            <div className="text-sm text-blue-900">
              <p className="font-medium">Note: Admin and Manager roles</p>
              <p className="text-blue-700">
                These roles have all permissions by default (superuser). Individual permission settings are overridden.
              </p>
            </div>
          </div>

          {/* Permissions Matrix */}
          <div className="overflow-x-auto">
            <div className="min-w-[800px]">
              {Object.entries(groupedPermissions).map(([category, perms]) => (
                <div key={category} className="mb-8">
                  <h3 className="text-lg font-semibold text-foreground mb-3 capitalize">
                    {category}
                  </h3>
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="text-left p-3 font-medium text-gray-700 w-1/4">
                            Permission
                          </th>
                          {ROLES.map(role => (
                            <th key={role.id} className="p-3 text-center">
                              <Badge className={`${role.color} text-white text-xs`}>
                                {role.name}
                              </Badge>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {perms.map(perm => (
                          <tr key={perm.id} className="border-t hover:bg-gray-50">
                            <td className="p-3">
                              <div>
                                <div className="font-medium text-sm">{perm.name}</div>
                                {perm.description && (
                                  <div className="text-xs text-gray-500 mt-0.5">
                                    {perm.description}
                                  </div>
                                )}
                              </div>
                            </td>
                            {ROLES.map(role => {
                              const enabled = hasPermission(role.id, perm.id);
                              const key = `${role.id}-${perm.id}`;
                              const isUpdating = updating === key;

                              return (
                                <td key={role.id} className="p-3 text-center">
                                  <button
                                    onClick={() => togglePermission(role.id, perm.id, enabled)}
                                    disabled={isUpdating}
                                    className={`inline-flex items-center justify-center w-8 h-8 rounded transition-colors ${
                                      enabled
                                        ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                        : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                                    } ${isUpdating ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                  >
                                    {isUpdating ? (
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : enabled ? (
                                      <Check className="h-5 w-5" />
                                    ) : (
                                      <X className="h-5 w-5" />
                                    )}
                                  </button>
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Legend */}
          <div className="pt-4 border-t">
            <div className="flex items-center gap-4 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded bg-green-100 text-green-700 flex items-center justify-center">
                  <Check className="h-5 w-5" />
                </div>
                <span>Enabled</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded bg-gray-100 text-gray-400 flex items-center justify-center">
                  <X className="h-5 w-5" />
                </div>
                <span>Disabled</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
